import { AxiosError } from 'axios';
import ajvFormats from 'ajv-formats';

import ApiError, { SerializedErrorDetails } from '../types/error';

export const isUUID = (id: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const isSerialNumber = (id: string): boolean => {
  const serialNumberRegex = /^[A-Za-z0-9]{10}$/;
  return serialNumberRegex.test(id);
};

export const isPCUUID = (id: string): boolean => {
  const pcUuidRegex =
    /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/;
  return pcUuidRegex.test(id);
};

// Sourced directly from ajv-formats — the same validator used by the backend
// via express-openapi-validator. Automatically stays in sync on yarn upgrade.
const EMAIL_REGEX = ajvFormats.get('email') as RegExp;

export const isEmail = (value: string): boolean => {
  if (value.length > 254) return false;

  const local = value.slice(0, value.indexOf('@'));

  return local.length <= 64 && EMAIL_REGEX.test(value);
};

export function unmarshalError(
  message: string,
  subError?: AxiosError
): ApiError {
  const details: SerializedErrorDetails | undefined = subError
    ? {
        message: subError.message,
        code: subError.code,
        statusCode: subError.response?.status
      }
    : undefined;
  const errorRet: ApiError = {
    message,
    details
  };
  return errorRet;
}

export const isCustomError = (v: ApiError): v is { message: string } =>
  v && typeof v === 'object' && typeof v.message === 'string';

/**
 * Utility function to determine if the polling interval should be cleared for a given key.
 *
 * @param value - The value to check (could be array or string id)
 * @param id - The id to compare
 * @param currentId - The current id stored for the key
 * @returns true if interval should be cleared, false otherwise
 */
export function shouldClearIntervalForKey(
  value: unknown,
  id: string,
  currentId: string | undefined
): boolean {
  return Array.isArray(value) || (currentId !== undefined && id !== currentId);
}

export const handleCopyCode = async (textToCopy: string): Promise<void> => {
  await navigator.clipboard.writeText(textToCopy);
};

export function getMaxAgeFromCacheControl(
  headers: Record<string, unknown>
): number | null {
  const raw = headers['cache-control'];
  if (!raw || (typeof raw !== 'string' && !Array.isArray(raw))) return null;

  // Cache-Control may be a string or array
  const cacheControl = Array.isArray(raw) ? raw.join(', ') : raw;

  const match = new RegExp(/max-age=(\d+)/i).exec(cacheControl);
  return match ? Number(match[1]) : null;
}
